/**
 * Dialogs, sheets, and a confirm that is part of the app rather than the browser.
 *
 * A native `confirm()` blocks the whole page, ignores the app's theme, and on iOS names
 * the site in the title bar. These read as the app because they are the app.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { useState, type ReactNode } from "react";

import { cn } from "../../lib/utils";
import { Button } from "./button";

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** "center" is a dialog on every size; "sheet" slides up from the bottom on phones. */
  variant?: "center" | "sheet" | "drawer";
  className?: string;
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  variant = "center",
  className,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-40 bg-ink/25 backdrop-blur-[2px]",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed z-50 flex flex-col overflow-hidden bg-paper",
            // Centred, and simply there: a dialog that slides or zooms reads as slower
            // than one that appears, and this one is usually answering a yes/no question.
            (variant === "center" || variant === "sheet") &&
              cn(
                "left-1/2 top-1/2 max-h-[85dvh] w-[min(30rem,calc(100vw-2rem))]",
                "-translate-x-1/2 -translate-y-1/2 rounded-2xl border border-rule shadow-xl",
                "data-[state=open]:animate-fade-in",
              ),
            variant === "drawer" &&
              cn(
                "inset-x-0 bottom-0 top-14 rounded-t-2xl border-t border-rule",
                "data-[state=open]:animate-slide-up data-[state=closed]:animate-slide-down-out",
                "md:inset-y-0 md:left-auto md:right-0 md:top-0 md:w-[27rem]",
                "md:rounded-none md:border-l md:border-t-0",
                "md:data-[state=open]:animate-slide-left",
                "md:data-[state=closed]:animate-slide-right-out",
              ),
            className,
          )}
          aria-describedby={description ? undefined : undefined}
        >
          <div className="flex items-start justify-between gap-3 border-b border-rule px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-base font-semibold">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-0.5 text-sm text-muted">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="-mr-2 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-ink/[0.06] hover:text-ink"
              >
                ✕
              </button>
            </Dialog.Close>
          </div>

          {children ? <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div> : null}

          {footer ? (
            <div className="flex flex-wrap justify-end gap-2 border-t border-rule px-5 py-3">
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
}

export function Confirm({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
}: ConfirmProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            autoFocus
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      {body ? <p className="text-sm text-muted">{body}</p> : null}
    </Modal>
  );
}

/** `const [ask, confirmDialog] = useConfirm()` — call `ask({...})` from an event handler. */
export function useConfirm(): [
  (options: Omit<ConfirmProps, "open" | "onOpenChange">) => void,
  ReactNode,
] {
  const [state, setState] = useState<Omit<ConfirmProps, "open" | "onOpenChange"> | null>(null);

  const element = state ? (
    <Confirm
      open
      onOpenChange={(open) => !open && setState(null)}
      {...state}
      onConfirm={() => {
        state.onConfirm();
        setState(null);
      }}
    />
  ) : null;

  return [setState, element];
}
